import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import AdminLayout from '../../components/AdminLayout'
import AnalisisIA from '../../components/AnalisisIA'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../lib/AuthContext'
import { getFechaReparto, getDiaRepartoLabel, isAfterCutoff } from '../../lib/entregasApi'
import { adminGetAlertasStock } from '../../lib/portalClienteApi'

export default function AdminDashboard() {
  const { user, role } = useAuth()
  const [stats, setStats] = useState(null)
  const [kpis, setKpis] = useState(null)
  const [misEntregas, setMisEntregas] = useState({ total: 0, pendientes: 0 })
  const [alertasStock, setAlertasStock] = useState([])

  useEffect(() => {
    async function load() {
      const fecha = getFechaReparto()

      // Rangos para KPIs (mes calendario actual: día 1 → último día)
      const hoy = new Date()
      const inicioMes = new Date(hoy.getFullYear(), hoy.getMonth(), 1).toISOString().slice(0, 10)
      const finMes = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0).toISOString().slice(0, 10)

      // Si es admin, ve TODAS las entregas del día (no solo las que tiene asignadas como repartidor)
      const esAdmin = role === 'admin'
      const queryEntregasHoy = esAdmin
        ? supabase.from('entregas').select('id, estado').eq('fecha_programada', fecha)
        : (user?.id
            ? supabase.from('entregas').select('id, estado').eq('repartidor_id', user.id).eq('fecha_programada', fecha)
            : Promise.resolve({ data: [] }))

      const [
        { count: clientesActivosTotal },
        { count: clientesEnZona },
        { count: clientesFueraZona },
        { count: porteros },
        { count: leads },
        { data: comisionesPendientes },
        { data: mias },
        alertas,
        // KPIs:
        { data: pagosMes },          // MRR del mes en curso
        { data: pagosTotal },        // LTV: suma all-time
        { count: cancelMesActual },  // churn: cancelaciones del mes
        { count: activosInicioMes }, // base para % churn (vivos al día 1)
        { count: entregasMes },      // entregas en el mes (con tope de fin de mes)
        { data: pedidosMes },        // ingresos one-shot mes (created_at fallback)
      ] = await Promise.all([
        supabase.from('clientes').select('*', { count: 'exact', head: true }).eq('estado', 'activa'),
        supabase.from('clientes').select('*', { count: 'exact', head: true }).eq('estado', 'activa').eq('fuera_zona', false),
        supabase.from('clientes').select('*', { count: 'exact', head: true }).eq('estado', 'activa').eq('fuera_zona', true),
        supabase.from('porteros').select('*', { count: 'exact', head: true }).eq('activo', true),
        supabase.from('leads').select('*', { count: 'exact', head: true }).eq('estado', 'nuevo'),
        supabase.from('comisiones').select('monto_total').eq('estado', 'pendiente'),
        queryEntregasHoy,
        adminGetAlertasStock(),
        supabase.from('pagos').select('monto').eq('estado', 'pagado').gte('pagado_at', inicioMes).lte('pagado_at', finMes + 'T23:59:59'),
        supabase.from('pagos').select('monto, cliente_id').eq('estado', 'pagado'),
        supabase.from('clientes').select('*', { count: 'exact', head: true }).eq('estado', 'cancelada').gte('cancelled_at', inicioMes).lte('cancelled_at', finMes + 'T23:59:59'),
        // Activos al día 1 del mes: empezaron antes del mes Y siguen vivos (cancelled_at null o posterior al inicio del mes)
        supabase.from('clientes').select('*', { count: 'exact', head: true }).lte('fecha_inicio', inicioMes).or(`cancelled_at.is.null,cancelled_at.gte.${inicioMes}`),
        supabase.from('entregas').select('*', { count: 'exact', head: true }).gte('fecha_programada', inicioMes).lte('fecha_programada', finMes),
        supabase.from('pedidos').select('monto_total').in('estado', ['pagado', 'en_armado', 'en_ruta', 'entregado']).gte('created_at', inicioMes).lte('created_at', finMes + 'T23:59:59'),
      ])

      const totalPendiente = (comisionesPendientes || []).reduce((s, c) => s + Number(c.monto_total), 0)
      const pendientes = (mias || []).filter(e => e.estado === 'programada' || e.estado === 'en_ruta').length
      setMisEntregas({ total: (mias || []).length, pendientes })
      setStats({ clientes: clientesActivosTotal, clientesEnZona, clientesFueraZona, porteros, leads, totalPendiente })
      setAlertasStock(alertas)

      // Cálculo KPIs
      const mrrMes = (pagosMes || []).reduce((s, p) => s + Number(p.monto || 0), 0)
      const oneShotMes = (pedidosMes || []).reduce((s, p) => s + Number(p.monto_total || 0), 0)
      const ingresosMes = mrrMes + oneShotMes
      const ltvTotal = (pagosTotal || []).reduce((s, p) => s + Number(p.monto || 0), 0)
      const clientesUnicosLtv = new Set((pagosTotal || []).map(p => p.cliente_id)).size
      const ltvProm = clientesUnicosLtv > 0 ? ltvTotal / clientesUnicosLtv : 0
      // Churn = cancelados del mes / activos al día 1
      const churnPct = activosInicioMes > 0 ? (cancelMesActual / activosInicioMes) * 100 : 0
      setKpis({
        mrrMes, oneShotMes, ingresosMes, ltvProm, churnPct, cancelMesActual,
        entregasMes,
        clientesActivos: clientesActivosTotal,
        clientesEnZona, clientesFueraZona,
      })
    }
    if (user?.id) load()
  }, [user?.id, role])

  const alertasCriticas = alertasStock.filter(a => a.nivel === 'critico')
  const alertasBajas = alertasStock.filter(a => a.nivel === 'bajo')

  const fechaLabel = getDiaRepartoLabel()
  const after = isAfterCutoff()

  return (
    <AdminLayout>
      <div className="page">
        <h1>Panel de administración</h1>
        <p className="lead">Resumen general · Limpack</p>
        {/* Acceso directo al Agente de Diseño y Comunicación */}
        <Link to="/admin/marketing-agent" className="agente-cta">
          <div className="agente-cta-icon">🤖</div>
          <div className="agente-cta-body">
            <div className="agente-cta-tag">AGENTE LIMPACK</div>
            <div className="agente-cta-title">Decime qué hay que <span>comunicar</span></div>
            <div className="agente-cta-sub">Brief en 2 líneas → el agente arma copy + slides según manual de marca v2.0.</div>
          </div>
          <div className="agente-cta-arrow">→</div>
          <style>{`
            .agente-cta {
              display: flex; align-items: center; gap: 16px;
              background: linear-gradient(135deg, #1A3461 0%, #00A8B5 100%);
              border-radius: 14px; padding: 16px 20px; margin-bottom: 16px;
              text-decoration: none; color: white;
              box-shadow: 0 8px 24px rgba(0,168,181,.18);
              transition: transform .15s, box-shadow .15s;
            }
            .agente-cta:hover {
              transform: translateY(-2px);
              box-shadow: 0 12px 32px rgba(0,168,181,.28);
            }
            .agente-cta-icon { font-size: 38px; line-height: 1; flex-shrink: 0; }
            .agente-cta-body { flex: 1; min-width: 0; }
            .agente-cta-tag {
              font-size: 11px; font-weight: 700; letter-spacing: .14em;
              color: #00D4E0; margin-bottom: 4px;
            }
            .agente-cta-title {
              font-size: 19px; font-weight: 800; letter-spacing: -.02em;
              line-height: 1.2;
            }
            .agente-cta-title span { color: #00D4E0; }
            .agente-cta-sub { font-size: 13px; opacity: .85; margin-top: 4px; line-height: 1.4; }
            .agente-cta-arrow {
              font-size: 28px; opacity: .7; flex-shrink: 0;
              transition: transform .15s, opacity .15s;
            }
            .agente-cta:hover .agente-cta-arrow { transform: translateX(4px); opacity: 1; }
            @media (max-width: 600px) {
              .agente-cta-icon { font-size: 30px; }
              .agente-cta-title { font-size: 16px; }
              .agente-cta-sub { font-size: 12px; }
              .agente-cta-arrow { display: none; }
            }
          `}</style>
        </Link>

        {/* Panel del dueño — KPIs clave del mes */}
        <div className="kpi-hero">
          <div className="kpi-hero-head">
            <div>
              <div className="kpi-hero-tag">📊 Panel del dueño · {new Date().toLocaleDateString('es-UY', { month: 'long', year: 'numeric' })}</div>
              <div className="kpi-hero-title">Salud del negocio</div>
            </div>
            <Link to="/admin/reportes" className="kpi-hero-link">Ver todos los reportes →</Link>
          </div>
          <div className="kpi-grid">
            <Link to="/admin/reportes/mrr-churn" className="kpi-card kpi-mrr">
              <div className="kpi-label">MRR del mes</div>
              <div className="kpi-value">${Math.round(kpis?.mrrMes || 0).toLocaleString('es-UY')}</div>
              <div className="kpi-sub">Suscripciones cobradas</div>
            </Link>
            <Link to="/admin/clientes" className="kpi-card kpi-activos">
              <div className="kpi-label">Suscriptores activos</div>
              <div className="kpi-value">{kpis?.clientesActivos ?? '—'}</div>
              <div className="kpi-sub">
                {kpis ? `${kpis.clientesEnZona ?? 0} en zona · ${kpis.clientesFueraZona ?? 0} fuera` : 'Estado "activa"'}
              </div>
            </Link>
            <Link to="/admin/reportes/mrr-churn" className="kpi-card kpi-churn">
              <div className="kpi-label">Churn del mes</div>
              <div className="kpi-value">{(kpis?.churnPct ?? 0).toFixed(1)}%</div>
              <div className="kpi-sub">{kpis?.cancelMesActual || 0} cancelaciones</div>
            </Link>
            <Link to="/admin/reportes/cohort-ltv" className="kpi-card kpi-ltv">
              <div className="kpi-label">LTV promedio</div>
              <div className="kpi-value">${Math.round(kpis?.ltvProm || 0).toLocaleString('es-UY')}</div>
              <div className="kpi-sub">Por cliente acumulado</div>
            </Link>
            <Link to="/admin/entregas" className="kpi-card kpi-entregas">
              <div className="kpi-label">Entregas del mes</div>
              <div className="kpi-value">{kpis?.entregasMes ?? '—'}</div>
              <div className="kpi-sub">Programadas + completadas</div>
            </Link>
            <Link to="/admin/reportes" className="kpi-card kpi-ingresos">
              <div className="kpi-label">Ingresos del mes</div>
              <div className="kpi-value">${Math.round(kpis?.ingresosMes || 0).toLocaleString('es-UY')}</div>
              <div className="kpi-sub">Susc + One-shot</div>
            </Link>
          </div>
          <style>{`
            .kpi-hero {
              background: linear-gradient(135deg, #0F1F3D 0%, #1a3461 100%);
              border-radius: 16px; padding: 22px;
              margin-bottom: 20px; color: #fff;
              box-shadow: 0 12px 32px rgba(15,31,61,.18);
            }
            .kpi-hero-head {
              display: flex; justify-content: space-between; align-items: flex-end;
              gap: 12px; flex-wrap: wrap; margin-bottom: 16px;
            }
            .kpi-hero-tag { font-size: 11px; opacity: .7; text-transform: uppercase; letter-spacing: .08em; font-weight: 700; }
            .kpi-hero-title { font-size: 20px; font-weight: 800; margin-top: 4px; }
            .kpi-hero-link {
              color: #00c4d4; text-decoration: none; font-weight: 700; font-size: 13px;
              padding: 8px 14px; background: rgba(0,196,212,.12);
              border: 1px solid rgba(0,196,212,.3); border-radius: 999px;
            }
            .kpi-hero-link:hover { background: rgba(0,196,212,.22); }
            .kpi-grid {
              display: grid;
              grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
              gap: 12px;
            }
            .kpi-card {
              background: rgba(255,255,255,.07);
              border: 1px solid rgba(255,255,255,.1);
              border-radius: 12px; padding: 14px 16px;
              text-decoration: none; color: #fff;
              transition: all .15s; display: block;
            }
            .kpi-card:hover {
              background: rgba(255,255,255,.13);
              border-color: rgba(0,196,212,.35);
              transform: translateY(-2px);
            }
            .kpi-label { font-size: 11px; opacity: .7; text-transform: uppercase; font-weight: 700; letter-spacing: .04em; }
            .kpi-value { font-size: 26px; font-weight: 900; margin: 4px 0; line-height: 1.1; }
            .kpi-sub { font-size: 11px; opacity: .65; }
            .kpi-mrr .kpi-value { color: #6ee7b7; }
            .kpi-activos .kpi-value { color: #93c5fd; }
            .kpi-churn .kpi-value { color: #fca5a5; }
            .kpi-ltv .kpi-value { color: #fcd34d; }
            .kpi-entregas .kpi-value { color: #c4b5fd; }
            .kpi-ingresos .kpi-value { color: #00c4d4; }
          `}</style>
        </div>

        {/* Análisis IA del estado del negocio */}
        {kpis && (
          <AnalisisIA
            reportType="dashboard_kpis"
            data={kpis}
            titulo="Análisis del mes"
          />
        )}

        {/* Hero card: Reparto del día (admin: todas · repartidor: las suyas) */}
        <Link to={role === 'admin' ? '/admin/entregas' : '/repartidor/hoy'} className="hero-mi-dia">
          <div className="hero-mi-dia-left">
            <div className="hero-mi-dia-tag">{after ? '🔒 MAÑANA' : '📦 HOY'} · {fechaLabel}</div>
            <div className="hero-mi-dia-title">
              {misEntregas.pendientes > 0
                ? (role === 'admin'
                    ? `${misEntregas.pendientes} entrega${misEntregas.pendientes !== 1 ? 's' : ''} para repartir`
                    : `Tenés ${misEntregas.pendientes} entrega${misEntregas.pendientes !== 1 ? 's' : ''} para repartir`)
                : misEntregas.total > 0
                  ? (role === 'admin' ? 'Todas las entregas del día completadas' : '¡Ya completaste tus entregas!')
                  : (role === 'admin' ? 'Sin entregas programadas hoy' : 'Sin entregas asignadas a vos')}
            </div>
            <div className="hero-mi-dia-sub">
              {misEntregas.pendientes > 0
                ? (role === 'admin' ? 'Ver entregas, asignar repartidor, ruta' : 'Ver mi ruta optimizada · Waze · marcar entregadas')
                : misEntregas.total === 0
                  ? (role === 'admin' ? 'Programar entregas en /admin/entregas' : 'Asignate algunas en /admin/entregas')
                  : 'Ver hechas'}
            </div>
          </div>
          <div className="hero-mi-dia-cta">
            <span className="hero-mi-dia-num">{misEntregas.pendientes}</span>
            <span className="hero-mi-dia-arrow">→</span>
          </div>
        </Link>

        {/* Alertas de stock — bien visible cuando hay críticos */}
        {alertasStock.length > 0 && (
          <div style={{
            background: alertasCriticas.length > 0 ? '#FEF2F2' : '#FEF3C7',
            border: alertasCriticas.length > 0 ? '2px solid #FCA5A5' : '2px solid #FCD34D',
            borderRadius: 14, padding: '16px 18px',
            marginBottom: 20,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 24 }}>{alertasCriticas.length > 0 ? '🚨' : '⚠️'}</span>
                <div>
                  <div style={{ fontWeight: 900, fontSize: 16, color: alertasCriticas.length > 0 ? '#7F1D1D' : '#78350F' }}>
                    Stock {alertasCriticas.length > 0 ? 'crítico' : 'bajo'}
                  </div>
                  <div style={{ fontSize: 12, color: alertasCriticas.length > 0 ? '#991B1B' : '#92400E' }}>
                    {alertasCriticas.length > 0 && (
                      <><strong>{alertasCriticas.length}</strong> producto{alertasCriticas.length !== 1 ? 's' : ''} en nivel crítico — comprar ya. </>
                    )}
                    {alertasBajas.length > 0 && (
                      <><strong>{alertasBajas.length}</strong> en nivel bajo.</>
                    )}
                  </div>
                </div>
              </div>
              <Link to="/admin/orden-compra" style={{
                background: '#0F1F3D', color: '#fff', padding: '8px 16px',
                borderRadius: 999, textDecoration: 'none', fontSize: 13, fontWeight: 800,
                whiteSpace: 'nowrap',
              }}>
                Generar orden de compra →
              </Link>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 240, overflowY: 'auto' }}>
              {alertasStock.slice(0, 8).map(a => (
                <Link key={a.sku} to={`/admin/stock/${a.sku}`} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  background: '#fff', padding: '8px 12px', borderRadius: 8,
                  textDecoration: 'none', fontSize: 13, color: '#0F1F3D',
                  border: a.nivel === 'critico' ? '1px solid #FCA5A5' : '1px solid #FCD34D',
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <strong style={{ fontWeight: 700 }}>{a.nombre}</strong>
                    <span style={{ color: '#64748b', marginLeft: 6, fontSize: 12 }}>
                      {a.marca && `· ${a.marca}`}{a.presentacion && ` · ${a.presentacion}`}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', whiteSpace: 'nowrap' }}>
                    <span style={{
                      fontWeight: 900, fontSize: 14,
                      color: a.nivel === 'critico' ? '#DC2626' : '#D97706',
                    }}>
                      {a.stock_actual}
                    </span>
                    <span style={{ fontSize: 11, color: '#64748b' }}>/ mín {a.stock_minimo}</span>
                    <span style={{
                      background: a.nivel === 'critico' ? '#DC2626' : '#F59E0B',
                      color: '#fff', padding: '2px 7px', borderRadius: 999,
                      fontSize: 9, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase',
                    }}>
                      {a.nivel === 'critico' ? 'CRÍTICO' : 'BAJO'}
                    </span>
                  </div>
                </Link>
              ))}
              {alertasStock.length > 8 && (
                <Link to="/admin/stock" style={{ textAlign: 'center', padding: '6px 0', color: '#0F1F3D', fontSize: 12, fontWeight: 700, textDecoration: 'underline' }}>
                  Ver todos ({alertasStock.length}) →
                </Link>
              )}
            </div>
          </div>
        )}

        <div className="cards-grid">
          <div className="card">
            <h3>Clientes activos</h3>
            <div className="stat">{stats?.clientes ?? '—'}</div>
            <div className="desc">Suscripciones en estado "activa"</div>
          </div>
          <div className="card">
            <h3>Porteros activos</h3>
            <div className="stat">{stats?.porteros ?? '—'}</div>
            <div className="desc">Programa de referidos en marcha</div>
          </div>
          <div className="card">
            <h3>Leads nuevos</h3>
            <div className="stat">{stats?.leads ?? '—'}</div>
            <div className="desc">Por contactar y convertir</div>
          </div>
          <div className="card">
            <h3>Comisiones a pagar</h3>
            <div className="stat">${Number(stats?.totalPendiente ?? 0).toLocaleString('es-UY')}</div>
            <div className="desc">Acumulado pendiente de transferencia</div>
          </div>
        </div>

        <div style={{ marginTop: 32, color: 'var(--muted)', fontSize: 13 }}>
          Próximas vistas: <strong>Clientes</strong>, <strong>Porteros</strong>, <strong>Leads</strong>, <strong>Comisiones</strong>, <strong>Pagos</strong>.
        </div>
      </div>
    </AdminLayout>
  )
}
